import {print} from 'graphql/language/printer';
import {post} from '../utils/ajaxHelper';
import {log as logToServer} from '../api/logger';
import {getGeneratedLocale} from '../utils/localeUtils';
import {evaluateExperiment} from './abacusHelper';
import {VRBO_TGA_PDP} from '../abacus/experiments';

const generateApolloClientHeaders = () => ({
    ['apollographql-client-name']: 'web_pdp',
    ['apollographql-client-version']: '2.0'
});

function handleGraphQlError(err, operationName, options) {
    const errors = (err && err.response && err.response.body && err.response.body.errors) || [];
    let message = 'An error occurred while processing your graphql request.';
    errors.forEach((currentError) => {
        logToServer({
            graphQlService: operationName,
            graphqlerror: true,
            message: currentError.message
        });
        message = currentError.message;
    });

    if (!errors.length) {
        logToServer({
            nodeService: `${operationName} exception`,
            graphqlerror: true,
            error: err, // hits up any serialization.
            options: JSON.stringify(options)
        });
    }

    // transform all the errors into something basic.
    const errorCode = (err.response && err.response.statusCode) || err.errorCode || 500;
    return {
        errorCode,
        originalErrorCode: errorCode, // backwards compatability
        message,
        errors
    };
}
/* eslint-disable complexity */
function mutateGraphQlResponse(response, operationName) {
    if (response.errorCode) {
        return response;
    }

    const graphqlRequestMarker =
        (response.headers && response.headers['x-homeaway-requestmarker']) || '';
    const responseBody = response.body || {data: {}};

    // We will fallback to statusCode.  I have seen a known bug where originalErrorCode is not
    // provided.
    const errorBlock =
        (responseBody.errors &&
            responseBody.errors.find((dataError) => {
                // We only care about an error if it has as statusCode in it.
                // this returns the FIRST error code
                return !!(dataError.extensions && dataError.extensions.statusCode);
            })) ||
        [];

    const pricingError = errorBlock.extensions || {};
    const graphQlErrorCode = pricingError.originalErrorCode || pricingError.statusCode || '';

    if (responseBody.errors && responseBody.errors.length) {
        responseBody.errors.forEach((currentError) => {
            logToServer({
                graphQlService: operationName,
                graphqlerror: true,
                message: currentError.message,
                graphqlRequestMarker
            });
        });
    }

    return {
        ...responseBody.data,
        originalErrorCode: graphQlErrorCode, // moved to top level for convenience
        errors: responseBody.errors, // keeping this for future proofing
        endTime: Date.now(),
        graphqlRequestMarker
    };
}
/* eslint-enable complexity */
/**
 * Handles POST to GraphQl Endpoint
 *
 * @param {*} query
 * @param {*} variables
 */
/* eslint-disable complexity */
async function graphqlQuery(operation, query, variables, locale, waitForAccessToken, store) {
    let gqlQueryString;
    try {
        gqlQueryString = print(query);
    } catch (e) {
        throw new Error(`Could not print query. Queries must be constructed using graphql-tag.`);
    }

    const options = {
        query: gqlQueryString,
        variables,
        operation
    };

    const shouldUseTga = evaluateExperiment({name: VRBO_TGA_PDP, variant: 1});

    const headers = {
        ...(shouldUseTga && generateApolloClientHeaders())
    };

    if (window && window.__SITE_CONTEXT__ && window.__SITE_CONTEXT__.name) {
        headers['x-homeaway-site'] = window.__SITE_CONTEXT__.name;
    }

    if (locale) {
        headers['X-HomeAway-DisplayLocale'] = getGeneratedLocale(locale);
    }

    // For selenium tests, use mocked data response from services
    // and bypass access token requirement which prevents the service request altogether
    const {useMockData} = (store && store.getState().systemReducer) || {};
    let url = `/${shouldUseTga ? 'mobileapi' : 'pdp'}/graphql`;
    let requireAccessToken = waitForAccessToken;

    if (useMockData) {
        url = `${url}?useMockData=true`;
        requireAccessToken = false;
    }

    const result = await post(url, options, headers, waitForAccessToken, requireAccessToken).catch(
        (err) => {
            return handleGraphQlError(err, operation, variables);
        }
    );

    // handleGraphQlError will add an errorCode, and already setup what we
    // want to return.
    if (!result.errorCode) {
        return mutateGraphQlResponse(result, operation);
    }

    return result;
}
/* eslint-enable complexity */

export default graphqlQuery;
export {graphqlQuery, handleGraphQlError, mutateGraphQlResponse, generateApolloClientHeaders};
